import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';

function Privacy() {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: November 27, 2025
        </Typography>

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              1. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              ComplainAI collects information you provide directly to us when you create an account, generate dispute letters, 
              or communicate with us. This includes:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>Account Information:</strong> Name, email address, username, and password
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Dispute Information:</strong> Company names, dispute descriptions, amounts claimed, and related correspondence
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Payment Information:</strong> Billing details processed securely through Stripe (we do not store credit card numbers)
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Usage Data:</strong> How you interact with our service, including pages viewed and features used
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              2. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Provide, maintain, and improve our AI-powered dispute letter generation service
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Process your transactions and send related notifications
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Send you technical notices, updates, and support messages
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Respond to your comments and questions
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Analyze usage patterns to improve our service
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              3. Information Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell, rent, or share your personal information with third parties except in the following circumstances:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>Service Providers:</strong> We work with third-party providers (like Stripe for payments and OpenAI/Grok for 
                AI generation) who help us operate our service
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Legal Requirements:</strong> If required by law or to protect our rights and safety
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of our business
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              <strong>Important Note:</strong> Your dispute information is used to generate personalized letters via AI. While we use 
              secure API connections, the content you provide is processed by third-party AI services (Grok AI) to generate your letters.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              4. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We take reasonable measures to protect your information from unauthorized access, including:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Encryption of data in transit using HTTPS/TLS
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Secure password hashing
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Regular security audits and updates
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Limited employee access to personal data
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              However, no security system is impenetrable. We cannot guarantee the absolute security of your information.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              5. Your Rights and Choices
            </Typography>
            <Typography variant="body1" paragraph>
              You have the following rights regarding your personal information:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>Access and Update:</strong> View and edit your account information through your profile settings
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Delete:</strong> Request deletion of your account and associated data by contacting us
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Export:</strong> Request a copy of your dispute letters and account data
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Opt-Out:</strong> Unsubscribe from marketing emails (you'll still receive service-related messages)
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              6. Cookies and Tracking
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar technologies to maintain your session, remember your preferences, and analyze site usage. 
              You can control cookies through your browser settings, but disabling them may affect site functionality.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              7. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your information for as long as your account is active or as needed to provide services. If you close your 
              account, we will delete or anonymize your personal information within 90 days, except where we must retain it for legal 
              compliance.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              8. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              ComplainAI is not intended for users under 18 years of age. We do not knowingly collect personal information from children. 
              If we learn we have collected information from a child under 18, we will delete it promptly.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              9. Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through our 
              service. Your continued use of ComplainAI after changes take effect constitutes acceptance of the updated policy.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              10. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about this Privacy Policy or our data practices, contact us at:
            </Typography>
            <Typography variant="body1" paragraph>
              Email: privacy@complain-ai.app<br />
              Address: ComplainAI, Inc., 123 Legal Street, Suite 100, San Francisco, CA 94103
            </Typography>
          </Box>

          <Box sx={{ mt: 6, p: 3, bgcolor: 'primary.main', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: 'white', fontStyle: 'italic' }}>
              <strong>Disclaimer:</strong> ComplainAI is NOT a credit repair service, law firm, or collection agency. We provide 
              AI-generated letter templates to help you communicate with companies. We do not provide legal advice. For legal matters, 
              consult a qualified attorney.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default Privacy;
